import { useEffect } from 'react'

import { useRouter } from 'next/compat/router'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import {
  Typography,
  Spacing,
  Breakpoints,
  Colors,
} from '@b-stock/fe-core/design-system'

import BreadcrumbsSection from '@/buyer-portal/components/features/listing/BreadcrumbsSection'
import ContractMaoTaxBannerContainer from '@/buyer-portal/components/features/listing/ContractMaoTaxBanner'
import { FirstDibsOpensBanner } from '@/buyer-portal/components/features/listing/FirstDibsBanner'
import { useRecordListingView } from '@/buyer-portal/components/features/listing/hooks/useRecordListingView'
import { useTrackPageViewed } from '@/buyer-portal/components/features/listing/hooks/useTrackPageViewed'
import { ListingActionColumn } from '@/buyer-portal/components/features/listing/ListingActionColumn'
import ListingDescription from '@/buyer-portal/components/features/listing/ListingDescription'
import { ListingTags } from '@/buyer-portal/components/features/listing/ListingTags'
import { MakeAnOfferSection } from '@/buyer-portal/components/features/listing/MakeAnOffer'
import ManifestButtons from '@/buyer-portal/components/features/listing/ManifestButtons'
import { ListingMediaCard } from '@/buyer-portal/components/features/listing/media/ListingMediaCard'
import MediaSection from '@/buyer-portal/components/features/listing/media/MediaSection'
import { OfferingInfoSection } from '@/buyer-portal/components/features/listing/OfferingInfo'
import RunningVatEstimateContainer from '@/buyer-portal/components/features/listing/RunningVatEstimate'
import {
  withScrollIntoView,
  ScrollIntoViewId,
  useScrollIntoView,
} from '@/buyer-portal/components/features/listing/ScrollIntoView/ScrollIntoViewContext'
import { SellerCollectsBanner } from '@/buyer-portal/components/features/listing/SellerCollectsBanner'
import { UnAuthWarning } from '@/buyer-portal/components/features/unAuthWarning'
import { withUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import { MoreFromSeller } from '@/components/listings/MoreFromSeller'
import { NotesPreviewWidget } from '@/components/listings/notes'
import { RecentlyViewedCarousel } from '@/components/listings/RecentlyViewedCarousel'
import { LdpPersonalizedCarousel } from '@/components/personalizedCategoryCarousel/LdpPersonalizedCarousel'
import { FeatureFlags } from '@/helpers/featureFlags'

import { ListingAttachmentsSection } from '../ListingAttachments'
import { ListingManifestSection } from '../ListingManifestSection'
import { ListingLotDescriptionSection } from '../LotDescription/ListingLotDescriptionSection'
import { PoliciesAccordion } from '../Policies'
import { SearchSimilarListings } from '../SearchSimilarListings'
import { ListingShipmentSection } from '../Shipment'
import ProblemFreeOrderRate from './facilityStats'
import { HazmatInfoBanner } from './HazmatInfoBanner'
import { ListingWatchlistCTA } from './ListingWatchlistCTA'
import { useListingDetailsLayoutTracking } from './useListingDetailsLayoutTracking'

const Title = styled.h1`
  ${Typography.Title1}
  color: ${Colors.Semantic.Neutral.black};
  margin: 1rem 0 1.5rem 0;
  align-self: stretch;
  @media ${Breakpoints.max.medium} {
    ${Typography.Title2}
    margin: 2rem 0 1.5rem 0;
  }
`

const AboveFoldContainer = styled.div`
  display: flex;
  flex-flow: wrap;
  gap: 2.5rem;
  @media ${Breakpoints.max.medium} {
    display: grid;
    grid-template-columns: 100%;
    grid-template-areas:
      'header'
      'actions'
      'details';
    gap: 2rem;
  }
`

const LeftContainer = styled.div`
  flex: 5 1 0%;
  min-width: 25rem;

  @media ${Breakpoints.max.medium} {
    display: contents;
  }
`

const HeaderContainer = styled.div`
  @media ${Breakpoints.max.medium} {
    grid-area: header;
  }
`

const TitleContainer = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: space-between;
`

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${Breakpoints.max.medium} {
    grid-area: details;
  }
`

const ActionsContainer = styled.div`
  flex: none;
  width: 27.75rem;

  @media ${Breakpoints.max.medium} {
    grid-area: actions;
    width: auto;
  }
`

const NotesWidgetSpacer = styled.div`
  margin-top: 2rem;
`

const BelowFoldContainer = styled.div`
  ${Spacing.Primitive.XL.css('gap')};
  display: flex;
  flex-direction: column;
  padding-top: 2rem;
`

const Container = styled.div`
  padding-bottom: 1rem;
  padding-top: 1rem;

  @media ${Breakpoints.max.medium} {
    padding-top: 0;
  }
`

const MediaSectionContainer = styled.div`
  ${Spacing.Primitive.L.css('padding-bottom')};
  ${Spacing.Primitive.L.css('padding-top')};
  order: 1;
  @media ${Breakpoints.max.medium} {
    order: 2;
  }
`

// ListingMediaCard is a bordered Card with no external margin of its own
// (unlike MediaSection, which this replaces) -- carry the same vertical
// rhythm here so it doesn't sit flush against its neighbors.
const MediaCardContainer = styled.div`
  ${Spacing.Primitive.L.css('padding-bottom')};
  ${Spacing.Primitive.L.css('padding-top')};
  order: 1;
  @media ${Breakpoints.max.medium} {
    order: 2;
  }
`

const ListingDescriptionContainer = styled.div`
  order: 2;

  @media ${Breakpoints.max.medium} {
    order: 1;
  }
`

const TagsManifestButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  ${Spacing.Primitive.L.css('gap')};

  @media ${Breakpoints.max.medium} {
    align-items: flex-start;
  }
`

const ListingDetailsLayoutBase = ({
  initialBidAmount,
  listing,
  referralUrl = '',
}: {
  initialBidAmount?: string
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
  referralUrl?: string
}) => {
  const { formatMessage } = useIntl()
  const router = useRouter()
  const isLoggedIn = !!useDecodedAccessToken()?.personId

  const enableMediaCard = useFlag(
    FeatureFlags.ENABLE_HP_LDP_ENHANCED_MEDIA_GALLERY
  )

  const { refs } = useScrollIntoView()

  useEffect(() => {
    if (!router?.isReady) return

    if (router.asPath.includes('#manifest-section')) {
      refs[ScrollIntoViewId.MANIFEST]?.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [router?.isReady, router?.asPath, refs])

  const title = listing.lot.title || formatMessage({ id: 'Common.unknown' })

  useTrackPageViewed({ listing, referralUrl })
  useListingDetailsLayoutTracking({ listing })
  useRecordListingView({ listingId: listing._id })

  return (
    <Container>
      <AboveFoldContainer>
        <LeftContainer>
          <HeaderContainer>
            <BreadcrumbsSection listing={listing} showLogo />
            <FirstDibsOpensBanner listing={listing} />
            <TitleContainer>
              <Title data-testid="lot.title">{title}</Title>
              <ListingWatchlistCTA listing={listing} />
            </TitleContainer>
            <ProblemFreeOrderRate listing={listing} />
            <TagsManifestButtonsContainer>
              <ListingTags listing={listing} />
              <ManifestButtons listing={listing} />
              <OfferingInfoSection listing={listing} />
            </TagsManifestButtonsContainer>
          </HeaderContainer>

          <DetailsContainer>
            <ListingDescriptionContainer>
              <HazmatInfoBanner listing={listing} />
              <ListingDescription listing={listing} />
            </ListingDescriptionContainer>
            {enableMediaCard ? (
              <MediaCardContainer>
                <ListingMediaCard listing={listing} />
              </MediaCardContainer>
            ) : (
              <MediaSectionContainer>
                <MediaSection listing={listing} />
              </MediaSectionContainer>
            )}
          </DetailsContainer>
        </LeftContainer>

        <ActionsContainer>
          <SellerCollectsBanner listing={listing} />
          <ListingActionColumn
            initialBidAmount={initialBidAmount}
            listing={listing}
          />
          <ContractMaoTaxBannerContainer listing={listing} />
          <RunningVatEstimateContainer listing={listing} />
          <NotesWidgetSpacer>
            <NotesPreviewWidget listing={listing} />
          </NotesWidgetSpacer>
          <SearchSimilarListings listing={listing} variant="sidebar" />
        </ActionsContainer>
      </AboveFoldContainer>
      <BelowFoldContainer>
        <MakeAnOfferSection listing={listing} />
        <ListingManifestSection listing={listing} />
        {isLoggedIn ? (
          <>
            <ListingLotDescriptionSection listing={listing} />
            <ListingShipmentSection listing={listing} />
            <ListingAttachmentsSection listing={listing} />
            <PoliciesAccordion listing={listing} />
          </>
        ) : (
          <UnAuthWarning
            reason={<FormattedMessage id="Listing.seeMoreListingDetails" />}
          />
        )}
        <MoreFromSeller listing={listing} />
        <RecentlyViewedCarousel excludeListingId={listing._id} />
        <LdpPersonalizedCarousel
          excludeListingId={listing._id}
          listingStorefrontId={listing.storefrontId}
        />
      </BelowFoldContainer>
    </Container>
  )
}

export const ListingDetailsLayout = withScrollIntoView(
  withUnAuthWarningModal(ListingDetailsLayoutBase)
)
